import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import type { PopupData, Service } from 'webapp-types';
import { MetricDetailsTab } from 'webapp/enums/enums';
import { AppEvent } from 'webapp/enums/events';
import { hideNativeNavigation, showNativeNavigation } from 'webapp/utils/native-bridge';

import didInsert from 'webapp/modifiers/did-insert';
import BarLoaderComponent from 'webapp/components/bar-loader';
import CommonPopupsDrawerComponent from 'webapp/components/common/popups/drawer';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import FormMetricSelectPopupComponent from 'webapp/components/form/metric-select/popup';
import MetricDetailsComponent from 'webapp/components/metric-details/index';
import type { Args } from 'webapp/components/popup/inner/modal';
import PopupInnerModalComponent from 'webapp/components/popup/inner/modal';

import MetricSetup from 'webapp/models-custom/metric-setup';

import type { NormalizedDetailState } from 'webapp/services/metric-details';

interface SwitchMetricEventData {
	metricSetup?: MetricSetup;
	state: NormalizedDetailState;
}

export default class MetricDetailsDrawerPopupComponent extends PopupInnerModalComponent<PopupData.Metric.DetailsDrawer> {
	@service('popup') declare popupService: Service.Popup;
	@service('model-manager/metrics') declare mmMetricsService: Service.ModelManager.Metrics;
	@service('model-manager/forecasts') declare mmForecastsService: Service.ModelManager.Forecasts;
	@service('event-manager') declare eventManagerService: Service.EventManager;
	@service('metric-details') declare metricDetailsService: Service.MetricDetails;

	node: HTMLElement | null = null;

	// needed to allow external tab switching (e.g. toast notification triggering forecast tab).
	@tracked activeTab: MetricDetailsTab = MetricDetailsTab.EXPLORE;
	@tracked pendingRequestId: string | null = null;
	@tracked initialState: NormalizedDetailState | null = null;
	// the mobile app hasn't loaded the metric models when the drawer opens
	@tracked areMetricsLoaded = true;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.initializeFromPopupData();
		this.setupEventListeners();

		if (this.metricDetailsService.isMobileMode) {
			hideNativeNavigation();
		} else {
			void this.mmForecastsService.requestForecastsData();
			void this.mmForecastsService.requestForecastHolidayOptions();
		}
	}

	get drawerState(): NormalizedDetailState | null {
		if (!this.initialState) return null;

		return {
			...this.initialState,
			// the mobile drawer has no Forecast tab
			tab: this.metricDetailsService.isMobileMode ? MetricDetailsTab.EXPLORE : this.activeTab,
			forecast: this.pendingRequestId
				? { requestId: this.pendingRequestId }
				: this.initialState.forecast,
		};
	}

	@action
	handleSwitchMetricEvent(eventData: SwitchMetricEventData) {
		if (!eventData.state) return;

		this.initialState = eventData.state;
		this.activeTab = eventData.state.tab || MetricDetailsTab.EXPLORE;

		const contentElement = this.node?.closest('.content') as HTMLElement | null,
			metricDetailsContent = contentElement?.querySelector(
				'.metric-details__content',
			) as HTMLElement | null;

		if (!metricDetailsContent) {
			return;
		}

		// close popup-metric-select if open
		const metricSelectPopup = this.popupService.findByComponent(FormMetricSelectPopupComponent);
		void metricSelectPopup?.close();

		// scroll the content element to top when metric is switched
		metricDetailsContent.scrollTo({ top: 0, behavior: 'smooth' });
	}

	@action
	setupView(element: HTMLElement): void {
		this.node = element;
	}

	override willDestroy() {
		super.willDestroy();
		this.eventManagerService.off(AppEvent.METRIC_INSIGHTS_SWITCH_METRIC, this);
		this.eventManagerService.off(AppEvent.FORECAST_DRAWER_SWITCH_TO_FORECAST_TAB, this);

		if (this.metricDetailsService.isMobileMode) {
			showNativeNavigation();
		}
	}

	private setupEventListeners(): void {
		this.eventManagerService.on(AppEvent.METRIC_INSIGHTS_SWITCH_METRIC, this, (eventData) => {
			this.handleSwitchMetricEvent(eventData);
		});

		if (this.metricDetailsService.isMobileMode) {
			return;
		}

		// subscribe to forecast tab switch events so toast can switch to forecast tab in drawer
		this.eventManagerService.on(
			AppEvent.FORECAST_DRAWER_SWITCH_TO_FORECAST_TAB,
			this,
			(eventData) => {
				this.switchTab(MetricDetailsTab.FORECAST, eventData.requestId);
			},
		);
	}

	private switchTab(tab: MetricDetailsTab, requestId?: string): void {
		this.activeTab = tab;
		this.pendingRequestId = requestId || null;
	}

	private initializeFromPopupData(): void {
		const data = this.popupData;

		this.initialState = data.state;
		this.activeTab = this.initialState?.tab || MetricDetailsTab.EXPLORE;

		if (this.metricDetailsService.isMobileMode) {
			this.loadMetricModels();
		} else {
			void this.mmMetricsService.prepareForMetricEditing();
		}
	}

	// Only what showing the metric needs, without the editing data.
	private loadMetricModels(): void {
		if (this.mmMetricsService.isMetricsRequested) {
			return;
		}

		this.areMetricsLoaded = false;
		void this.mmMetricsService.requestMetrics().finally(() => {
			if (this.isDestroying || this.isDestroyed) {
				return;
			}

			this.areMetricsLoaded = true;
			this.trackMetricViewed();
		});
	}

	// Opening tracks the view only for a metric that's loaded, so the first mobile open tracks it here.
	private trackMetricViewed(): void {
		const metricSetupData = this.initialState?.shared?.metricSetup;
		const metric = metricSetupData?.key ? new MetricSetup(metricSetupData).metric : null;

		if (metric) {
			this.metricDetailsService.trackMetricInsightsViewed(this.popupData.origin, metric);
		}
	}
	<template>
		<CommonPopupsDrawerComponent>
			<:content>
				<div id="page-metric-details" {{didInsert this.setupView}}>
					{{#if this.areMetricsLoaded}}
						<MetricDetailsComponent
							@initialState={{this.drawerState}}
							@onClose={{this.close}}
						/>
					{{else}}
						<div class="metric-explorer metric-details__loading">
							<a
								role="button"
								class="metric-details__close"
								{{on "click" this.close}}
							>
								<CommonUiIconComponent @name="Close" @size="lg" />
							</a>
							<BarLoaderComponent @size="big" />
						</div>
					{{/if}}
				</div>
			</:content>
		</CommonPopupsDrawerComponent>
	</template>
}
